import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Send, Loader2, MapPin, Info, Search, Eye, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { PstBisDialog, pstBisOffered } from './PstBisDialog';
import { cn, mapUrl } from '@/lib/utils';
import { ApiError } from '@/lib/api-client';
import { AddressComboBox, type ComboOption } from '@/components/forms/AddressComboBox';
import {
  createAddress,
  findNeighbor,
  fetchReleaseNeighbors,
  geocodeAddress,
  searchPostalCodes,
  searchCities,
  searchMunicipalities,
  searchStreets,
  searchAreas,
  searchPops,
} from '@/hooks/useAddressCreate';
import { useDmpWriteConfirm } from '@/hooks/useDmpWriteConfirm';
import { fetchTicketEnrichment } from '@/hooks/useTicketEnrichment';
import { formatDeliveryStatus, isValidDeliveryCode, isBlockedDeliveryCode, houseNumberExtensionCoherent, houseNumberDigitsOnly } from '@uniticket/shared';
import type { CreateAddressRequest, FiberCascadeOption, ReleaseNeighbor, ReleaseNeighborsResult } from '@uniticket/shared';
import type { AddressMgmtContext } from './AddressManagementMenu';

// Standalone "Create an address" form (port of the DMP /address/create + AddressCreateFormType). Mirrors
// ReleaseAddressPanel: per-field useState, client validation collecting missing[], inline error by the
// button (no request fires until it passes), toast on success. Writes the LOCAL copy only (the server
// refuses unless ADDR_TABLE_TARGET=local). Address dropdowns read the real DMP referential read-only;
// the fiber cascade reads the local seeded infra.

// Wallonia Lambert-72 bounding box — MUST match the server zod bounds (address-create.ts).
const X_MIN = 40000,
  X_MAX = 300000,
  Y_MIN = 20000,
  Y_MAX = 170000;

// Fiber cascade options are {id, text}; the combobox speaks {value, label}.
const toCombo = (opts: FiberCascadeOption[]): ComboOption[] => opts.map((o) => ({ value: String(o.id), label: o.text }));

// A ticket-context string → a pre-selected combobox option (value === label); null when absent.
const toOpt = (v?: string | null): ComboOption | null => (v ? { value: v, label: v } : null);

export function CreateAddressForm({ onCreated, context }: { onCreated: () => void; context?: AddressMgmtContext }) {
  const confirmDmpWrite = useDmpWriteConfirm();
  const { t } = useTranslation('tickets');

  // ── Address fields — pre-filled from the ticket context when present (all still editable). The strict
  // cascade municipality→city→postal→street is seeded so the operator only corrects what's wrong. ──
  const [municipality, setMunicipality] = useState<ComboOption | null>(() => toOpt(context?.municipality));
  const [city, setCity] = useState<ComboOption | null>(() => toOpt(context?.city));
  const [postalCode, setPostalCode] = useState<ComboOption | null>(() => toOpt(context?.postalCode));
  const [street, setStreet] = useState<ComboOption | null>(() => toOpt(context?.streetName));
  const [area, setArea] = useState<ComboOption | null>(null);
  // House number / extension / DUT / in-scope pre-fill from the context (the "MDU not in Appeee" Cas-3 flow
  // passes the request's parsed hnr_full + DUT='MDU'); all still editable.
  const [houseNumber, setHouseNumber] = useState(() => context?.houseNumber ?? '');
  const [houseExt, setHouseExt] = useState(() => context?.houseNumberExtension ?? '');
  // Pré-rempli depuis le contexte (Cas 3 « MDU not in Appeee » : la demande porte un `box_nmb`), parce
  // que c'est ce champ qui décide du type de logement à la création.
  const [bus, setBus] = useState(() => context?.busNumber ?? '');
  const [apartment, setApartment] = useState('');
  const [x, setX] = useState('');
  const [y, setY] = useState('');
  const [delivery, setDelivery] = useState('');
  const [lengthEstimate, setLengthEstimate] = useState('');
  const [inScope, setInScope] = useState(() => context?.inScope ?? true);
  // « Release immédiatement » (user decision 2026-07-31): default ON — a create used to end invisible
  // to the partner unless someone found the discreet Release button afterwards. Forced off out of scope.
  // EXCEPT from the DMP console (user decision 2026-08-11): a creation with no ticket behind it has no
  // partner waiting on it, so it starts UNRELEASED — and therefore outside the SharePoint push to the
  // sub-contractor file, whose gate is precisely the release. The admin ticks it deliberately when the
  // address really is meant to be published.
  const [releaseNow, setReleaseNow] = useState(() => !context?.viaConsole);
  const releaseChecked = inScope && releaseNow;

  // Les voisins sont-ils released ? (demande du user, 17-09) — « vous demandez de release, mais vos voisins ne le
  // sont pas : il y a quelque chose à regarder ». Lu tout seul dès que la rue et le numéro sont là ET que la case
  // Release est cochée : c'est le seul cas où la réponse change quelque chose. `neighborsReq` écarte une réponse
  // arrivée après un changement d'adresse.
  const [neighbors, setNeighbors] = useState<{
    status: 'idle' | 'loading' | 'ready' | 'error';
    data: ReleaseNeighborsResult | null;
  }>({ status: 'idle', data: null });
  const neighborsReq = useRef(0);
  // Le choix fait dans la fenêtre d'avertissement vaut pour CES voisins-là : il retombe dès qu'ils changent. Sans
  // lui, un « Créer quand même » (garde de similarité) reposerait la question à laquelle on vient de répondre.
  const [neighborsChoice, setNeighborsChoice] = useState<'release' | 'noRelease' | null>(null);
  const [neighborsAsk, setNeighborsAsk] = useState<{ allowSimilar: boolean } | null>(null);
  // N29.1 : la question PST bis en attente (avec le choix des voisins déjà fait), et la dernière réponse donnée —
  // réutilisée par le seul « Créer quand même » de la garde de similarité, qui relance CETTE soumission-là.
  const [pstBisAsk, setPstBisAsk] = useState<{ allowSimilar: boolean; neighborsChoice?: 'release' | 'noRelease' } | null>(null);
  const lastPstBis = useRef<boolean | undefined>(undefined);

  // ── Fake ODF (optional): prepare ONE placeholder fiber on a PoP. Only the PoP is chosen — rack/panel/port
  // are fixed at "0" (the PoP's available slot, assigned later by network engineering). ──
  // Off by default from the console, for the same reason: nothing should be reserved on a PoP for an
  // address nobody ordered. It also spares the admin a step — ticking this makes the PoP MANDATORY
  // (see the guard in submit), which would otherwise force a fiber decision on every console creation.
  const [createFakeOdf, setCreateFakeOdf] = useState(() => !context?.viaConsole);
  const [pop, setPop] = useState<ComboOption | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The similar-address guard refused (409): offer the explicit « Créer quand même » override.
  const [similarBlocked, setSimilarBlocked] = useState(false);
  const [attempted, setAttempted] = useState(false); // gates the per-field red border (only after a submit try)
  const [neighborLoading, setNeighborLoading] = useState(false);
  const [neighborNote, setNeighborNote] = useState<string | null>(null);
  // ICAR geocoding → auto-fill the Lambert X/Y. `coordsManual` flips true once the reviewer types X/Y by
  // hand, which suppresses the auto-geocode so it never clobbers a manual coordinate. geoReq guards stale
  // responses (only the latest call applies). geoNote shows the matched house + the ICAR match quality.
  const [geocoding, setGeocoding] = useState(false);
  const [geoNote, setGeoNote] = useState<string | null>(null);
  const [geoNoteError, setGeoNoteError] = useState(false); // red when the note is a not-found/not-exact/error
  const [coordsManual, setCoordsManual] = useState(false);
  // Mirror coordsManual into a ref so the async geocode callback reads its LATEST value (and never clears a
  // coordinate the reviewer typed while a request was in flight).
  const coordsManualRef = useRef(coordsManual);
  coordsManualRef.current = coordsManual;
  const geoReq = useRef(0);
  const [popNote, setPopNote] = useState<string | null>(null);

  // Pre-fill the Fake-ODF PoP from the ticket's address enrichment (the PoP the enrichment panel shows,
  // e.g. "JODO-1A"). The enrichment carries the PoP NAME; resolve it to the point_of_presence id the fakeOdf
  // payload needs via searchPops. Best-effort + one-shot; never clobbers a PoP already chosen (operator /
  // neighbour). Runs only when the form has the open ticket's id.
  useEffect(() => {
    const ticketId = context?.ticketId;
    if (!ticketId) return;
    let alive = true;
    (async () => {
      try {
        const enr = await fetchTicketEnrichment(ticketId);
        const popName = enr.data?.addressStatus?.pop?.trim();
        if (!alive || !popName) return;
        const matches = await searchPops(popName);
        const match = matches.find((p) => p.text === popName) ?? matches[0];
        if (!alive || !match) return;
        setPop((cur) => cur ?? { value: String(match.id), label: match.text });
        setPopNote(t('addressMgmt.create.popFromEnrichment', { pop: match.text }));
      } catch {
        /* best-effort — the operator can still pick a PoP manually */
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context?.ticketId]);

  // ── Live-derived validation signals ──
  const xNum = Number(x);
  const yNum = Number(y);
  const xyTouched = x.trim() !== '' && y.trim() !== '';
  const xyValid =
    xyTouched &&
    !Number.isNaN(xNum) &&
    !Number.isNaN(yNum) &&
    xNum >= X_MIN &&
    xNum <= X_MAX &&
    yNum >= Y_MIN &&
    yNum <= Y_MAX;
  const deliveryNum = delivery.trim() === '' ? null : Number(delivery);
  const deliveryValid = deliveryNum == null || (Number.isInteger(deliveryNum) && isValidDeliveryCode(deliveryNum));
  // REQUIRED distance (user rule 2026-07-31): whole metres, street → building.
  const lengthNum = lengthEstimate.trim() === '' ? null : Number(lengthEstimate);
  const lengthValid = lengthNum != null && Number.isInteger(lengthNum) && lengthNum >= 0;

  // ── Cascade resets (changing a parent invalidates its children) ──
  // Strict escalating cascade (mirrors unifiber-api.be): municipality → city → postal code → street.
  // Changing a level invalidates every level below it. (Inline error is cleared by comboField's onChange.)
  function changeMunicipality(opt: ComboOption | null) {
    setMunicipality(opt);
    setCity(null);
    setPostalCode(null);
    setStreet(null);
  }
  function changeCity(opt: ComboOption | null) {
    setCity(opt);
    setPostalCode(null);
    setStreet(null);
  }
  function changePostal(opt: ComboOption | null) {
    setPostalCode(opt);
    setStreet(null);
  }

  // "Check the neighbour": pre-fill area + delivery_status from the nearest same-parity neighbour on the
  // same street. Needs the street (cascade complete) + a numeric house number.
  async function handleNeighbor() {
    if (!street || houseNumber.trim() === '') return;
    setNeighborLoading(true);
    setNeighborNote(null);
    try {
      const r = await findNeighbor({
        street: street.value,
        houseNumber: houseNumber.trim(),
        postalCode: postalCode?.value,
        city: city?.value,
        municipality: municipality?.value,
      });
      if (!r.found) {
        setNeighborNote(t('addressMgmt.create.neighborNotFound'));
        return;
      }
      if (r.areaName) setArea({ value: r.areaName, label: r.areaName });
      if (r.deliveryStatus != null) setDelivery(String(r.deliveryStatus));
      // Fiber from the neighbour → enable "Create fake ODF" and pre-fill the PoP. Rack/panel/port stay at 0
      // (the PoP's available slot); the real placement is assigned later.
      if (r.fiber) {
        setCreateFakeOdf(true);
        setPop({ value: String(r.fiber.pop.id), label: r.fiber.pop.text });
      }
      setError(null);
      setNeighborNote(
        t(r.fiber ? 'addressMgmt.create.neighborFoundFiber' : 'addressMgmt.create.neighborFound', {
          houseNumber: r.houseNumber,
        }),
      );
    } catch {
      setNeighborNote(t('addressMgmt.create.neighborError'));
    } finally {
      setNeighborLoading(false);
    }
  }

  // House-level match quality from the ICAR composite score (score % 10) → a short human label.
  function geoQuality(code?: number): string {
    switch (code) {
      case 1: return t('addressMgmt.create.geoQualExact');
      case 2: return t('addressMgmt.create.geoQualCleaned');
      case 3:
      case 4: return t('addressMgmt.create.geoQualNearby');
      case 5: return t('addressMgmt.create.geoQualSegment');
      case 6: return t('addressMgmt.create.geoQualCentroid');
      default: return t('addressMgmt.create.geoQualUnknown');
    }
  }

  // Geocode the current address via ICAR and fill X/Y. `manual` = triggered by the button (vs the auto
  // effect). Overwrites X/Y unless the reviewer typed coordinates by hand; a superseded response (the
  // address changed mid-request) is dropped via geoReq.
  async function runGeocode(manual: boolean) {
    if (!street || houseNumber.trim() === '') return;
    const myReq = ++geoReq.current;
    setGeocoding(true);
    if (manual) setGeoNote(null);
    try {
      const r = await geocodeAddress({
        street: street.value,
        house: houseNumber.trim(),
        postalCode: postalCode?.value,
        city: city?.value,
        municipality: municipality?.value,
      });
      if (myReq !== geoReq.current) return; // a newer request superseded this one
      // A non-fill outcome must drop any stale AUTO-filled coordinates (never a manually-typed one).
      const dropAutoCoords = () => { if (!coordsManualRef.current) { setX(''); setY(''); } };
      if (!r.found || r.x == null || r.y == null) {
        dropAutoCoords();
        setGeoNoteError(true);
        setGeoNote(t('addressMgmt.create.geoNotFound'));
        return;
      }
      // Only auto-fill when ICAR matched the EXACT house number (match code 1). For a number that isn't in
      // the referential yet ICAR returns the nearest house on the segment — we must NOT fill X/Y with that;
      // we tell the reviewer the exact number wasn't found so they enter the coordinates by hand.
      if (r.houseMatchCode !== 1) {
        dropAutoCoords();
        setGeoNoteError(true);
        setGeoNote(t('addressMgmt.create.geoNotExact', { house: r.requestedHouse ?? houseNumber.trim() }));
        return;
      }
      setX(String(Math.round(r.x)));
      setY(String(Math.round(r.y)));
      setCoordsManual(false);
      setError(null);
      setGeoNoteError(false);
      setGeoNote(
        t('addressMgmt.create.geoFilled', {
          house: r.matchedHouse ?? r.requestedHouse ?? houseNumber.trim(),
          quality: geoQuality(r.houseMatchCode),
        }),
      );
    } catch {
      if (myReq === geoReq.current) { setGeoNoteError(true); setGeoNote(t('addressMgmt.create.geoError')); }
    } finally {
      if (myReq === geoReq.current) setGeocoding(false);
    }
  }

  // Auto-fill X/Y whenever the address (street + house, with its commune/postal) settles — unless the
  // reviewer has typed coordinates by hand. Debounced; re-runs on any address change so the point tracks
  // the current address. The button re-runs it on demand.
  useEffect(() => {
    if (coordsManual) return;
    if (!street || houseNumber.trim() === '') return;
    const id = setTimeout(() => { runGeocode(false); }, 700);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [street?.value, houseNumber, postalCode?.value, city?.value, municipality?.value, coordsManual]);

  // Voisins released ? — même rythme que le géocodage (700 ms après la dernière frappe).
  useEffect(() => {
    const house = houseNumber.trim();
    const myReq = ++neighborsReq.current;
    setNeighborsChoice(null);
    if (!releaseChecked || !street || !/^\d+/.test(house)) {
      setNeighbors({ status: 'idle', data: null });
      return;
    }
    setNeighbors({ status: 'loading', data: null });
    const id = setTimeout(() => {
      fetchReleaseNeighbors({
        street: street.value,
        houseNumber: house,
        postalCode: postalCode?.value,
        city: city?.value,
        municipality: municipality?.value,
      })
        .then((data) => { if (myReq === neighborsReq.current) setNeighbors({ status: 'ready', data }); })
        .catch(() => { if (myReq === neighborsReq.current) setNeighbors({ status: 'error', data: null }); });
    }, 700);
    return () => clearTimeout(id);
  }, [releaseChecked, street?.value, houseNumber, postalCode?.value, city?.value, municipality?.value]);

  const neighborList = neighbors.data
    ? [neighbors.data.lower, neighbors.data.higher].filter((n): n is ReleaseNeighbor => n != null)
    : [];
  // Un voisin dont AUCUN logement n'est released. Un immeuble à moitié publié n'alerte pas : c'est la rue qui
  // n'est pas encore ouverte qui compte, pas le dernier logement en retard.
  const unreleasedNeighbors = neighborList.filter((n) => n.released === 0);
  const neighborLine = (n: ReleaseNeighbor) =>
    t('addressMgmt.create.neighborLine', { number: n.houseNumber, released: n.released, units: n.units, inScope: n.inScope });

  const houseExtIncoherent = !houseNumberExtensionCoherent(houseNumber, houseExt);
  // Digits-only house number (user decision 2026-07-31, dup 626/633: "7C" typed into the number).
  const houseNumNotDigits = houseNumber.trim() !== '' && !houseNumberDigitsOnly(houseNumber);

  // Options, jamais un booléen nu : les boutons l'appellent à travers une flèche (un onClick direct passerait le
  // MouseEvent en premier argument). `neighborsChoice` porte la réponse que la fenêtre d'avertissement vient de
  // donner — l'état, lui, ne sera à jour qu'au rendu suivant.
  async function handleSubmit(opts: { allowSimilar?: boolean; neighborsChoice?: 'release' | 'noRelease'; pstBis?: boolean } = {}) {
    const allowSimilar = opts.allowSimilar ?? false;
    setAttempted(true);
    if (!allowSimilar) setSimilarBlocked(false);
    const missing: string[] = [];
    if (!postalCode) missing.push(t('addressMgmt.create.postalCode'));
    if (!city) missing.push(t('addressMgmt.create.city'));
    if (!municipality) missing.push(t('addressMgmt.create.municipality'));
    if (!street) missing.push(t('addressMgmt.create.street'));
    if (houseNumber.trim() === '') missing.push(t('addressMgmt.create.houseNumber'));
    if (houseExt.trim() === '') missing.push(t('addressMgmt.create.houseExt'));
    if (missing.length > 0) {
      setError(t('addressMgmt.create.errRequired', { fields: missing.join(', ') }));
      return;
    }
    if (!houseNumberDigitsOnly(houseNumber)) {
      setError(t('addressMgmt.create.errHouseNumberDigits'));
      return;
    }
    if (!houseNumberExtensionCoherent(houseNumber, houseExt)) {
      setError(t('validation.houseExtIncoherent'));
      return;
    }
    if (!xyValid) {
      setError(t('addressMgmt.create.errWallonia'));
      return;
    }
    if (!deliveryValid) {
      setError(t('addressMgmt.create.errDeliveryCode'));
      return;
    }
    if (!lengthValid) {
      setError(t('addressMgmt.create.errLengthRequired'));
      return;
    }
    if (createFakeOdf && !pop) {
      setError(t('addressMgmt.create.errPopRequired'));
      return;
    }

    // Voisins non released (17-09) : on le dit AVANT d'écrire, et le relecteur choisit — release quand même, créer
    // sans release, ou revenir au formulaire. Jamais bloquant, et une vérification en échec ne demande rien.
    const choice = opts.neighborsChoice ?? neighborsChoice;
    const release = releaseChecked && choice !== 'noRelease';
    if (release && unreleasedNeighbors.length > 0 && choice !== 'release') {
      setNeighborsAsk({ allowSimilar });
      return;
    }

    // N29.1 — « Build doit-il avoir cette adresse ? », APRÈS la question des voisins, jamais par-dessus. Sa réponse
    // relance cette même fonction, choix des voisins compris ; « Revenir au formulaire » n'écrit rien.
    const offered = pstBisOffered(context);
    if (offered && opts.pstBis === undefined) {
      setPstBisAsk({ allowSimilar, neighborsChoice: choice ?? undefined });
      return;
    }
    lastPstBis.current = offered ? opts.pstBis : undefined;

    // Fake ODF: send just the chosen PoP; the server attaches the fiber to its "0/0/0" available slot.
    const fakeOdf = createFakeOdf && pop ? { popId: Number(pop.value) } : undefined;

    const body: CreateAddressRequest = {
      // Non-null asserted: the missing[] guard above already returned if any of these is null.
      municipality: municipality!.value,
      city: city!.value,
      postalCode: postalCode!.value,
      streetName: street!.value,
      areaName: area?.value ?? null,
      x: xNum,
      y: yNum,
      deliveryStatus: deliveryNum,
      lengthEstimateFromStreet: lengthNum!,
      inDeploymentScope: inScope,
      houseNumber: houseNumber.trim(),
      houseNumberExtension: houseExt.trim(),
      appartmentNumber: apartment.trim() || null,
      busNumber: bus.trim() || null,
      fakeOdf,
      ticketNumber: context?.ticketNumber ?? null,
      // Auto-link (user ask 2026-07-30): when the open ticket has no jvid, the server links the new
      // address to it — the window then reopens in normal validation mode (reload below).
      ticketId: context?.ticketId ?? null,
      ...(allowSimilar ? { allowSimilar: true } : {}),
      ...(release ? { releaseImmediately: true } : {}),
      // Ce que le relecteur a vu avant de confirmer le release, pour l'audit.
      ...(release && unreleasedNeighbors.length > 0 && neighbors.data ? { releaseNeighborsWarning: neighbors.data } : {}),
      // N29.1 : la réponse, seulement quand la question a été posée — absente, le serveur lit « Non, pas proposée ».
      ...(offered ? { pstBis: opts.pstBis === true } : {}),
    };

    setError(null);
    if (!confirmDmpWrite('créer cette adresse')) return;
    setSubmitting(true);
    try {
      const res = await createAddress(body, context?.viaConsole);
      toast.success(t(res.data.released ? 'addressMgmt.create.createdReleased' : 'addressMgmt.create.created', { jvid: res.data.jvid }));
      if (offered && opts.pstBis === true) toast.success(t('addressMgmt.pstBis.queued'));
      if (res.data.releaseError) {
        toast.error(t('addressMgmt.create.releaseFailed', { error: res.data.releaseError }));
      }
      if (res.data.linkedTicket) {
        // The no-address ticket just got its address: full reload → normal validation mode.
        window.location.reload();
        return;
      }
      onCreated();
    } catch (err: unknown) {
      setError((err as Error)?.message || t('addressMgmt.create.error'));
      // The similar-guard 409 (its message is our own backend constant) → offer the explicit override.
      if (err instanceof ApiError && err.status === 409 && /very similar address/i.test(err.message)) {
        setSimilarBlocked(true);
      }
    } finally {
      setSubmitting(false);
    }
  }

  // ── Small render helpers ──
  function comboField(
    labelKey: string,
    field: ComboOption | null,
    onChange: (o: ComboOption | null) => void,
    fetcher: (q: string) => Promise<ComboOption[]>,
    opts?: { required?: boolean; disabled?: boolean; missing?: boolean; className?: string; allowCustom?: boolean },
  ) {
    return (
      <div className={opts?.className}>
        <label className="mb-1 block text-xs font-medium text-slate-600">
          {t(labelKey)} {opts?.required && <span className="text-red-500">*</span>}
        </label>
        <AddressComboBox
          value={field?.value ?? ''}
          label={field?.label ?? ''}
          onChange={(o) => {
            onChange(o);
            setError(null);
          }}
          fetcher={fetcher}
          disabled={opts?.disabled}
          error={opts?.missing}
          allowCustom={opts?.allowCustom}
          customLabel={t('addressMgmt.create.comboUseCustom')}
          placeholder={t('addressMgmt.create.comboPlaceholder')}
          emptyText={t('addressMgmt.create.comboNoResults')}
          searchingText={t('addressMgmt.create.comboSearching')}
          clearLabel={t('addressMgmt.create.clear')}
        />
      </div>
    );
  }

  function textField(
    labelKey: string,
    value: string,
    set: (v: string) => void,
    opts?: { required?: boolean; className?: string; invalid?: boolean },
  ) {
    return (
      <div className={opts?.className}>
        <label className="mb-1 block text-xs font-medium text-slate-600">
          {t(labelKey)} {opts?.required && <span className="text-red-500">*</span>}
        </label>
        <Input
          value={value}
          onChange={(e) => {
            set(e.target.value);
            setError(null);
          }}
          className={cn(opts?.invalid && 'border-red-300 bg-red-50 focus-visible:ring-red-200')}
        />
      </div>
    );
  }

  // Read-only "0" for the Fake-ODF rack/panel/port — immutable (the PoP's available slot, assigned later).
  function zeroField(labelKey: string) {
    return (
      <div>
        <label className="mb-1 block text-xs font-medium text-slate-600">{t(labelKey)}</label>
        <div className="flex h-10 items-center rounded-lg border border-slate-200 bg-slate-50 px-4 text-sm font-semibold text-slate-500">
          0
        </div>
      </div>
    );
  }

  return (
    <div>
      <SheetTitle className="flex items-center gap-2">
        <MapPin className="h-5 w-5 text-brand-rose" /> {t('addressMgmt.create.title')}
      </SheetTitle>
      <SheetDescription className="mt-1">{t('addressMgmt.create.subtitle')}</SheetDescription>

      {/* ── Address section ── */}
      <h3 className="mb-2 mt-5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        {t('addressMgmt.create.sectionAddress')}
      </h3>
      <div className="grid grid-cols-2 gap-3">
        {/* Strict escalating cascade — order matters: municipality → city → postal code → street. */}
        {comboField('addressMgmt.create.municipality', municipality, changeMunicipality, searchMunicipalities, {
          required: true,
          className: 'col-span-2',
          missing: attempted && !municipality,
        })}
        {comboField(
          'addressMgmt.create.city',
          city,
          changeCity,
          (q) => searchCities(q, { municipality: municipality?.value }),
          { required: true, disabled: !municipality, missing: attempted && !city },
        )}
        {comboField(
          'addressMgmt.create.postalCode',
          postalCode,
          changePostal,
          (q) => searchPostalCodes(q, { municipality: municipality?.value, city: city?.value }),
          { required: true, disabled: !city, missing: attempted && !postalCode, allowCustom: true },
        )}
        {comboField(
          'addressMgmt.create.street',
          street,
          setStreet,
          (q) => searchStreets(q, { municipality: municipality?.value, city: city?.value, postalCode: postalCode?.value }),
          { required: true, className: 'col-span-2', disabled: !postalCode, missing: attempted && !street, allowCustom: true },
        )}

        {textField('addressMgmt.create.houseNumber', houseNumber, setHouseNumber, { required: true, invalid: houseNumNotDigits || (attempted && houseExtIncoherent) })}
        {textField('addressMgmt.create.houseExt', houseExt, setHouseExt, { required: true, invalid: attempted && houseExtIncoherent })}
        {/* Live warning as soon as a letter lands in the NUMBER — the letter belongs in the extension. */}
        {houseNumNotDigits && (
          <p className="col-span-2 -mt-2 text-[11px] font-medium text-red-600" role="alert">
            {t('addressMgmt.create.errHouseNumberDigits')}
          </p>
        )}
        {textField('addressMgmt.create.bus', bus, setBus)}
        {textField('addressMgmt.create.apartment', apartment, setApartment)}

        {/* Dwelling-unit type — DERIVED, never chosen (user rule 2026-07-27), et dérivé du NUMÉRO DE
            BOÎTE : une ligne qui porte une boîte est un logement d'immeuble (MDU), sans boîte c'est
            l'ancre du bâtiment (SDU), qui basculera à la première boîte ajoutée. La pastille suit donc
            le champ « Boîte » en direct — c'est la meilleure preuve que le type n'est pas un choix. */}
        <div className="col-span-2">
          <label className="mb-1 block text-xs font-medium text-slate-600">
            {t('addressMgmt.create.dwellingUnitType')}
          </label>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center rounded-md bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600">
              {bus.trim() ? 'MDU' : 'SDU'}
            </span>
            <span className="text-[11px] text-slate-400">
              {t('addressMgmt.create.dwellingUnitTypeAutoHint')}
            </span>
          </div>
        </div>

        {/* C0.5 ④ (décision du user du 07-10 : avertir SANS bloquer) — une adresse créée AVEC une boîte est un
            logement d'immeuble créé de toutes pièces : rien ne dit qu'il est construit (cas du 06-10, ~19 adresses
            créées puis released sur un immeuble que personne n'avait construit). Le bandeau suit le champ « Boîte »
            en direct, comme la pastille du type ; le serveur trace la création. Jamais dans la file MDU
            (`context.inScope`) : y créer le logement demandé est le geste normal. */}
        {bus.trim() !== '' && !context?.inScope && (
          <div className="col-span-2 rounded-lg border-2 border-amber-400 bg-amber-50 px-3 py-2.5" role="note">
            <p className="flex items-center gap-1.5 text-sm font-bold text-amber-900">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {t('addressMgmt.unbuilt.title')}
            </p>
            <p className="mt-1 text-xs text-amber-900">{t('addressMgmt.unbuilt.createBody')}</p>
          </div>
        )}

        {/* X / Y — Lambert-72 metres, validated live against the Wallonia bbox. */}
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">
            {t('addressMgmt.create.x')} <span className="text-red-500">*</span>
          </label>
          <Input
            type="number"
            value={x}
            onChange={(e) => {
              setX(e.target.value);
              setCoordsManual(true);
              setError(null);
            }}
            className={cn(xyTouched && !xyValid && 'border-red-300')}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">
            {t('addressMgmt.create.y')} <span className="text-red-500">*</span>
          </label>
          <Input
            type="number"
            value={y}
            onChange={(e) => {
              setY(e.target.value);
              setCoordsManual(true);
              setError(null);
            }}
            className={cn(xyTouched && !xyValid && 'border-red-300')}
          />
        </div>
        <p className="col-span-2 -mt-1.5 text-[10px] text-slate-400">{t('addressMgmt.create.xyHint')}</p>

        {/* ICAR geocoding — auto-fills X/Y from the address (SPW Wallonia); the button re-runs it on demand. */}
        <div className="col-span-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => runGeocode(true)}
            disabled={!street || houseNumber.trim() === '' || geocoding}
            className="w-full"
          >
            {geocoding ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <MapPin className="mr-1.5 h-4 w-4" />}
            {t('addressMgmt.create.geoButton')}
          </Button>
          {geoNote && (
            <p className={cn('mt-1 text-[10px]', geoNoteError ? 'font-medium text-red-600' : 'text-slate-500')}>{geoNote}</p>
          )}
        </div>

        {/* "Check the neighbour" — pre-fill Area + delivery_status from the nearest same-parity neighbour
            on the same street (automates the manual lookup). Reads the DMP referential read-only. */}
        <div className="col-span-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleNeighbor}
            disabled={!street || houseNumber.trim() === '' || neighborLoading}
            className="w-full"
          >
            {neighborLoading ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Search className="mr-1.5 h-4 w-4" />
            )}
            {t('addressMgmt.create.neighborButton')}
          </Button>
          {neighborNote && <p className="mt-1 text-[10px] text-slate-500">{neighborNote}</p>}
        </div>

        {/* Area (optional, DMP referential) — not persisted; pre-fillable from the neighbour above. */}
        {comboField('addressMgmt.create.area', area, setArea, searchAreas, { className: 'col-span-2' })}

        {/* delivery_status — optional 4-digit composite with a live decoded preview. */}
        <div className="col-span-2">
          <label className="mb-1 block text-xs font-medium text-slate-600">{t('addressMgmt.create.deliveryStatus')}</label>
          <Input
            type="number"
            value={delivery}
            onChange={(e) => {
              setDelivery(e.target.value);
              setError(null);
            }}
            className={cn(delivery.trim() !== '' && !deliveryValid && 'border-red-300')}
            placeholder={t('addressMgmt.create.deliveryPlaceholder')}
          />
          {delivery.trim() !== '' && (
            <p className={cn(
              'mt-1 text-[10px]',
              !deliveryValid ? 'text-red-500'
                : isBlockedDeliveryCode(deliveryNum) ? 'font-semibold text-red-600'
                : 'text-slate-500',
            )}>
              {deliveryValid ? formatDeliveryStatus(deliveryNum) : t('addressMgmt.create.errDeliveryCode')}
            </p>
          )}
        </div>

        {/* length_estimate_from_street — REQUIRED (user rule 2026-07-31): measured street→building;
            stored at create and fed to the chained release so the 7xxx + >28 m quote rule fires. */}
        <div className="col-span-2">
          <label className="mb-1 block text-xs font-medium text-slate-600">
            {t('validation.release.lengthEstimate')} <span className="text-red-500">*</span>
          </label>
          <Input
            type="number"
            value={lengthEstimate}
            onChange={(e) => {
              setLengthEstimate(e.target.value);
              setError(null);
            }}
            className={cn(lengthEstimate.trim() !== '' && !lengthValid && 'border-red-300')}
            placeholder={t('addressMgmt.create.lengthPlaceholder')}
          />
          {lengthEstimate.trim() !== '' && !lengthValid && (
            <p className="mt-1 text-[10px] text-red-500">{t('addressMgmt.create.errLengthRequired')}</p>
          )}
          {lengthValid && deliveryNum != null && deliveryNum >= 7000 && deliveryNum <= 7999 && lengthNum! > 28 && (
            <p className="mt-1 text-[10px] text-amber-600">{t('validation.attention.rules.quoteDistance.releaseHint')}</p>
          )}
        </div>

        {/* Street View — keyless Google Maps link to eyeball the installation type before setting the
            delivery status. The form only has Lambert-72 x/y (no lat/lon), so we open Maps at the address
            and the reviewer drops the pegman into Street View. */}
        <div className="col-span-2">
          {street && houseNumber.trim() !== '' ? (
            <Button asChild variant="outline" className="w-full">
              <a
                href={mapUrl([street.label, houseExt.trim() || houseNumber, postalCode?.label, city?.label])}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Eye className="mr-1.5 h-4 w-4" />
                {t('addressMgmt.create.streetView')}
              </a>
            </Button>
          ) : (
            <Button type="button" variant="outline" className="w-full" disabled>
              <Eye className="mr-1.5 h-4 w-4" />
              {t('addressMgmt.create.streetView')}
            </Button>
          )}
          <p className="mt-1 text-[10px] text-slate-400">{t('addressMgmt.create.streetViewHint')}</p>
        </div>

        {/* In-deployment-scope → scopelist_v15 = today. */}
        <label className="col-span-2 mt-1 flex items-center gap-2 text-xs font-medium text-slate-600">
          <input
            type="checkbox"
            checked={inScope}
            onChange={(e) => setInScope(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-brand-rose focus:ring-brand-rose/30"
          />
          {t('addressMgmt.create.inScope')}
        </label>
        <label className={cn('mt-2 flex items-center gap-2 text-xs font-semibold', inScope ? 'text-slate-700' : 'text-slate-400')}>
          <input
            type="checkbox"
            checked={inScope && releaseNow}
            disabled={!inScope}
            onChange={(e) => setReleaseNow(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-brand-rose focus:ring-brand-rose/30 disabled:opacity-50"
          />
          {t('addressMgmt.create.releaseNow')}
        </label>
        <p className="mt-1 text-[10px] text-slate-400">{t('addressMgmt.create.releaseNowHint')}</p>

        {/* Voisins released ? (17-09) — en ambre franc quand un voisin n'a AUCUN logement released : il faut que
            ça attire l'œil avant le clic, pas seulement dans la fenêtre qui suit. */}
        {releaseChecked && neighbors.status === 'loading' && (
          <p className="col-span-2 flex items-center gap-1.5 text-[11px] text-slate-500">
            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
            {t('addressMgmt.create.neighborsChecking')}
          </p>
        )}
        {releaseChecked && neighbors.status === 'error' && (
          <p className="col-span-2 text-[11px] text-slate-500">{t('addressMgmt.create.neighborsError')}</p>
        )}
        {releaseChecked && neighbors.status === 'ready' && (
          unreleasedNeighbors.length > 0 ? (
            <div className="col-span-2 rounded-lg border-2 border-amber-400 bg-amber-50 px-3 py-2.5" role="alert">
              <p className="flex items-center gap-1.5 text-sm font-bold text-amber-900">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {t('addressMgmt.create.neighborsNotReleasedTitle')}
              </p>
              <p className="mt-1 text-xs text-amber-900">{t('addressMgmt.create.neighborsNotReleasedBody')}</p>
              <ul className="mt-1.5 space-y-0.5 text-xs font-medium text-amber-900">
                {neighborList.map((n) => (
                  <li key={n.houseNumber} className={cn(n.released > 0 && 'font-normal text-amber-800/80')}>
                    {neighborLine(n)}
                  </li>
                ))}
              </ul>
            </div>
          ) : neighborList.length > 0 ? (
            <p className="col-span-2 text-[11px] text-emerald-700">
              {t('addressMgmt.create.neighborsReleased', { numbers: neighborList.map((n) => n.houseNumber).join(', ') })}
            </p>
          ) : (
            <p className="col-span-2 text-[11px] text-slate-500">{t('addressMgmt.create.neighborsNone')}</p>
          )
        )}
      </div>

      {/* ── Fake ODF section (replaces the fiber cascade) ── */}
      <div className="mt-5 border-t border-slate-100 pt-4">
        <label className="flex items-center gap-2 text-xs font-semibold text-slate-700">
          <input
            type="checkbox"
            checked={createFakeOdf}
            onChange={(e) => {
              setCreateFakeOdf(e.target.checked);
              setError(null);
            }}
            className="h-4 w-4 rounded border-slate-300 text-brand-rose focus:ring-brand-rose/30"
          />
          {t('addressMgmt.create.createFakeOdf')}
        </label>

        {createFakeOdf && (
          <div className="mt-3 space-y-3">
            <p className="flex items-start gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2 text-[10px] text-slate-500">
              <Info className="mt-0.5 h-3 w-3 shrink-0" />
              {t('addressMgmt.create.fakeOdfNote')}
            </p>

            {/* PoP — the only editable fiber field (full real list, searchable; pre-filled from the neighbour). */}
            {comboField('addressMgmt.create.pop', pop, setPop, (q) => searchPops(q).then(toCombo), {
              required: true,
              missing: attempted && createFakeOdf && !pop,
            })}
            {popNote && <p className="-mt-1 text-[10px] text-slate-500">{popNote}</p>}

            {/* Rack / Patch Panel / Port — fixed at 0 (the PoP's available slot), immutable. */}
            <div className="grid grid-cols-3 gap-3">
              {zeroField('addressMgmt.create.rack')}
              {zeroField('addressMgmt.create.patchPanel')}
              {zeroField('addressMgmt.create.port')}
            </div>
          </div>
        )}
      </div>

      {/* ── Submit ── */}
      {error && (
        <p className="mt-4 text-xs font-medium text-red-600" role="alert">
          {error}
        </p>
      )}
      {/* Similar-guard override (user decision 2026-07-31): the reviewer read the refusal above and
          confirms the address is genuinely different (e.g. a distinct extension, Gilly 171 vs 171 A). */}
      {similarBlocked && (
        <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5">
          <p className="text-xs text-amber-800">{t('addressMgmt.create.similarOverrideHint')}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={submitting}
            // N29.1 : la réponse PST bis de la soumission que la garde vient de bloquer — ne pas la redemander.
            onClick={() => void handleSubmit({ allowSimilar: true, pstBis: lastPstBis.current })}
            className="mt-2 border-amber-400 text-amber-800 hover:bg-amber-100"
          >
            {t('addressMgmt.create.createAnyway')}
          </Button>
        </div>
      )}
      {/* Pendant la vérification des voisins, on n'envoie pas : un clic rapide passerait avant l'avertissement. */}
      <Button
        onClick={() => void handleSubmit()}
        disabled={submitting || (releaseChecked && neighbors.status === 'loading')}
        className="mt-4 w-full"
      >
        {submitting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}
        {submitting ? t('addressMgmt.create.submitting') : t('addressMgmt.create.submit')}
      </Button>

      <ConfirmDialog
        open={neighborsAsk != null}
        tone="amber"
        icon={<AlertTriangle className="h-5 w-5" />}
        title={t('addressMgmt.create.neighborsAskTitle')}
        message={
          <>
            <p>{t('addressMgmt.create.neighborsAskBody')}</p>
            <ul className="mt-2 space-y-0.5 text-xs font-medium text-amber-900">
              {neighborList.map((n) => <li key={n.houseNumber}>{neighborLine(n)}</li>)}
            </ul>
          </>
        }
        confirmLabel={t('addressMgmt.create.neighborsAskRelease')}
        cancelLabel={t('addressMgmt.create.neighborsAskCancel')}
        secondaryLabel={t('addressMgmt.create.neighborsAskNoRelease')}
        onSecondary={() => {
          const allow = neighborsAsk?.allowSimilar ?? false;
          setNeighborsAsk(null);
          setReleaseNow(false);
          void handleSubmit({ allowSimilar: allow, neighborsChoice: 'noRelease' });
        }}
        onCancel={() => setNeighborsAsk(null)}
        onConfirm={() => {
          const allow = neighborsAsk?.allowSimilar ?? false;
          setNeighborsAsk(null);
          setNeighborsChoice('release');
          void handleSubmit({ allowSimilar: allow, neighborsChoice: 'release' });
        }}
      />

      {/* N29.1 — la dernière question avant d'écrire (voir PstBisDialog). */}
      <PstBisDialog
        open={pstBisAsk != null}
        kind="address"
        viaConsole={!!context?.viaConsole}
        refusal={context?.buildDeclineMode}
        busy={submitting}
        onYes={() => { const a = pstBisAsk ?? { allowSimilar: false }; setPstBisAsk(null); void handleSubmit({ ...a, pstBis: true }); }}
        onNo={() => {
          const a = pstBisAsk ?? { allowSimilar: false }; setPstBisAsk(null);
          // N29.4 : sans ticket (console), « Non » crée sans PST bis ; sur un ticket, rien n'est créé, la page prépare le refus.
          if (context?.viaConsole) void handleSubmit({ ...a, pstBis: false });
          else context?.onBuildDeclined?.();
        }}
        onCancel={() => setPstBisAsk(null)}
      />
    </div>
  );
}
